import React from 'react';
import { UniversalDropdown, UniversalDropdownWithLabel } from './universal-dropdown';
import { useAuthStore } from '../../store/authStore';

// Универсальный компонент селекта с правильными стилями
interface UniversalSelectProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: Array<{ value: string | number; label: string }>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  openUpward?: boolean;
}

export const UniversalSelect: React.FC<UniversalSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  disabled = false,
  required = false,
  openUpward = false
}) => {
  return (
    <UniversalDropdown
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      required={required}
      openUpward={openUpward}
    />
  );
};

// Компонент селекта с лейблом
interface SelectWithLabelProps extends UniversalSelectProps {
  label: string;
  error?: string | null;
}

export const SelectWithLabel: React.FC<SelectWithLabelProps> = ({
  label,
  error,
  ...selectProps
}) => {
  return (
    <UniversalDropdownWithLabel
      label={label}
      error={error}
      {...selectProps}
    />
  );
};

const getRoleLevel = (role: string): number => {
  const normalizedRole = String(role).toUpperCase();
  switch (normalizedRole) {
    case 'SUPER_ADMIN':
      return 4;
    case 'OWNER':
      return 3;
    case 'ADMIN':
      return 2;
    case 'RECRUITER':
      return 1;
    default:
      return 0;
  }
};

interface RoleSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  includeAll?: boolean;
  targetUserRole?: string;
  isEditingSelf?: boolean;
  disabled?: boolean;
  hasCompany?: boolean;
  companyHasOwner?: boolean;
}

export const RoleSelect: React.FC<RoleSelectProps> = ({
  value,
  onChange,
  className = '',
  includeAll = false,
  targetUserRole,
  isEditingSelf = false,
  disabled = false,
  hasCompany = false,
  companyHasOwner = false
}) => {
  const authState = useAuthStore.getState();
  const currentRole = String(authState.user?.role || authState.role || '').toUpperCase();
  const currentUserLevel = getRoleLevel(currentRole);
  
  const allRoleOptions = [
    { value: 'SUPER_ADMIN', label: 'Суперадминистратор', level: 4 },
    { value: 'OWNER', label: 'Владелец', level: 3 },
    { value: 'ADMIN', label: 'Администратор', level: 2 },
    { value: 'RECRUITER', label: 'Рекрутер', level: 1 }
  ];
  
  const targetUserLevel = targetUserRole ? getRoleLevel(targetUserRole) : 0;
  const isBlocked = disabled || isEditingSelf || (!!targetUserRole && targetUserLevel >= currentUserLevel);
  
  const isTargetUserOwner = targetUserRole?.toUpperCase() === 'OWNER';
  const canSetOwner = hasCompany && (!companyHasOwner || isTargetUserOwner);
  
  let roleOptions;
  if (isBlocked && targetUserRole) {
    roleOptions = allRoleOptions.filter(option => option.value === targetUserRole.toUpperCase());
  } else {
    const normalizedCurrentRole = String(currentRole).toUpperCase();
    roleOptions = allRoleOptions.filter(option => {
      if (option.value === 'SUPER_ADMIN') return false;
      if (option.value === 'OWNER') {
        if (normalizedCurrentRole !== 'SUPER_ADMIN') return false;
        return canSetOwner;
      }
      return option.level <= currentUserLevel;
    });
  }

  const options = includeAll 
    ? [{ value: '', label: 'Все роли' }, ...roleOptions]
    : roleOptions;

  const displayValue = isBlocked && targetUserRole ? targetUserRole.toUpperCase() : value;

  return (
    <UniversalSelect
      value={displayValue}
      onChange={(val) => onChange(String(val))}
      options={options}
      className={className}
      disabled={isBlocked}
    />
  );
};

interface RoleSelectWithLabelProps extends RoleSelectProps {
  label: string;
  error?: string | null;
}

const getRoleLabel = (role: string): string => {
  switch (String(role).toUpperCase()) {
    case 'SUPER_ADMIN':
      return 'суперадминистратора';
    case 'OWNER':
      return 'владельца';
    case 'ADMIN':
      return 'администратора';
    case 'RECRUITER':
      return 'рекрутера';
    default:
      return 'пользователя';
  }
};

export const RoleSelectWithLabel: React.FC<RoleSelectWithLabelProps> = ({
  label,
  error,
  ...roleSelectProps
}) => {
  const authState = useAuthStore.getState();
  const currentRole = String(authState.user?.role || authState.role || '').toUpperCase();
  const currentUserLevel = getRoleLevel(currentRole);
  const targetUserLevel = roleSelectProps.targetUserRole 
    ? getRoleLevel(roleSelectProps.targetUserRole) 
    : 0;
  
  const isRoleChangeBlocked = roleSelectProps.isEditingSelf || 
    (!!roleSelectProps.targetUserRole && targetUserLevel >= currentUserLevel);
  
  const isSuperAdmin = currentRole === 'SUPER_ADMIN';
  const isTargetUserOwner = roleSelectProps.targetUserRole?.toUpperCase() === 'OWNER';
  const ownerNotAvailableDueToNoCompany = isSuperAdmin && !roleSelectProps.hasCompany && !isRoleChangeBlocked;
  const ownerNotAvailableDueToExisting = isSuperAdmin && roleSelectProps.hasCompany && 
    roleSelectProps.companyHasOwner && !isTargetUserOwner && !isRoleChangeBlocked;
  
  const helperText = roleSelectProps.isEditingSelf 
    ? 'Нельзя изменять свою роль'
    : (roleSelectProps.targetUserRole && targetUserLevel >= currentUserLevel)
    ? `Нельзя изменять роль ${getRoleLabel(roleSelectProps.targetUserRole)}`
    : ownerNotAvailableDueToNoCompany
    ? 'Чтобы назначить роль "Владелец", необходимо выбрать компанию'
    : ownerNotAvailableDueToExisting
    ? 'В этой компании уже есть владелец'
    : null;

  return (
    <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
      <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
        <p className="block leading-[20px]">{label}</p>
      </label>
      <RoleSelect {...roleSelectProps} />
      {(isRoleChangeBlocked || ownerNotAvailableDueToNoCompany || ownerNotAvailableDueToExisting) && helperText && (
        <div className="text-[#868c98] text-[12px] font-['Inter:Regular',_sans-serif] mt-1">
          {helperText}
        </div>
      )}
      {error && (
        <div className="text-red-500 text-[12px] font-['Inter:Regular',_sans-serif] mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

// Специализированный селект для компаний
interface CompanySelectProps {
  value: string | number;
  onChange: (value: string | number) => void;
  companies: Array<{ id: number; name: string }>;
  className?: string;
  includeAll?: boolean;
  includeNone?: boolean;
  openUpward?: boolean;
  disabled?: boolean;
}

export const CompanySelect: React.FC<CompanySelectProps> = ({
  value,
  onChange,
  companies,
  className = '',
  includeAll = false,
  includeNone = false,
  openUpward = false,
  disabled = false
}) => {
  const options = [];
  
  if (includeAll) {
    options.push({ value: '', label: 'Все компании' });
  }
  
  if (includeNone) {
    options.push({ value: 'null', label: 'Без компании' });
  }
  
  if (!includeAll && !includeNone) {
    options.push({ value: '', label: 'Без компании' });
  }
  
  options.push(...companies.map(company => ({
    value: company.id,
    label: company.name
  })));

  return (
    <UniversalSelect
      value={value}
      onChange={onChange}
      options={options}
      className={className}
      openUpward={openUpward}
      disabled={disabled}
    />
  );
};

// Специализированный селект для компаний с лейблом
interface CompanySelectWithLabelProps extends CompanySelectProps {
  label: string;
  error?: string | null;
}

export const CompanySelectWithLabel: React.FC<CompanySelectWithLabelProps> = ({
  label,
  error,
  ...companySelectProps
}) => {
  return (
    <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
      <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
        <p className="block leading-[20px]">{label}</p>
      </label>
      <CompanySelect {...companySelectProps} />
      {error && (
        <div className="text-red-500 text-[12px] font-['Inter:Regular',_sans-serif] mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

// Специализированный селект для статусов компаний
interface CompanyStatusSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  includeAll?: boolean;
}

export const CompanyStatusSelect: React.FC<CompanyStatusSelectProps> = ({
  value,
  onChange,
  className = '',
  includeAll = false
}) => {
  const statusOptions = [
    { value: 'ACTIVE', label: 'Активная' },
    { value: 'SUSPENDED', label: 'Приостановлена' },
    { value: 'INACTIVE', label: 'Неактивная' }
  ];

  const options = includeAll 
    ? [{ value: '', label: 'Все статусы' }, ...statusOptions]
    : statusOptions;

  return (
    <UniversalSelect
      value={value}
      onChange={(val) => onChange(String(val))}
      options={options}
      className={className}
    />
  );
};

// Специализированный селект для статусов компаний с лейблом
interface CompanyStatusSelectWithLabelProps extends CompanyStatusSelectProps {
  label: string;
  error?: string | null;
}

export const CompanyStatusSelectWithLabel: React.FC<CompanyStatusSelectWithLabelProps> = ({
  label,
  error,
  ...statusSelectProps
}) => {
  return (
    <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
      <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
        <p className="block leading-[20px]">{label}</p>
      </label>
      <CompanyStatusSelect {...statusSelectProps} />
      {error && (
        <div className="text-red-500 text-[12px] font-['Inter:Regular',_sans-serif] mt-1">
          {error}
        </div>
      )}
    </div>
  );
};
